import { AuthProvider } from 'react-admin';
import { apiClient } from './apiClient';

const removeUserFromLocalStorage = () => {
  localStorage.removeItem('user');
  localStorage.removeItem('auth_token');
  localStorage.removeItem('refresh_token');
};

export const authProvider: AuthProvider = {
  // Called when the user attempts to log in
  login: async ({ username, password }) => {
    try {
      const response = await apiClient.login({ email: username, password });
      
      // Check if user has ADMIN role
      if (response.user.role !== 'ADMIN') {
        throw new Error('Access denied. Admin role required.');
      }
      
      // Store user data
      localStorage.setItem('user', JSON.stringify(response.user));
      
      return Promise.resolve();
    } catch (error: any) {
      console.error('Login failed:', error);
      throw new Error(error.response?.data?.message || error.message || 'Login failed');
    }
  },

  // Called when the user clicks on the logout button
  logout: async () => {
    try {
      await apiClient.logout();
    } catch (error) {
      console.error('Logout error:', error);
      // Continue with logout even if API call fails
    } finally {
      removeUserFromLocalStorage();
    }
    return Promise.resolve();
  },

  // Called when the API returns an error
  checkError: ({ status }: { status: number }) => {
    if (status === 401 || status === 403) {
     removeUserFromLocalStorage();
      return Promise.reject({ redirectTo: '/login' });
    }
    return Promise.resolve();
  },

  // Called when the user navigates to a new location, to check for authentication
  checkAuth: () => {
    const token = localStorage.getItem('auth_token');
    const user = localStorage.getItem('user');
    
    if (!token || !user) {
      return Promise.reject();
    }
    
    try {
      const userData = JSON.parse(user);
      // Double-check admin role
      if (userData.role !== 'ADMIN') {
        localStorage.removeItem('user');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        return Promise.reject();
      }
      
      return Promise.resolve();
    } catch (error) {
      return Promise.reject();
    }
  },

  // Called when the user navigates to a new location, to check for permissions / roles
  getPermissions: () => {
    const user = localStorage.getItem('user');
    if (!user) {
      return Promise.reject();
    }
    
    try {
      const userData = JSON.parse(user);
      return Promise.resolve(userData.role);
    } catch (error) {
      return Promise.reject();
    }
  },

  // Called when the user profile is requested
  getIdentity: async () => {
    // Use stored user data to avoid circular API calls
    const user = localStorage.getItem('user');
    if (!user) {
      return Promise.reject();
    }
    
    try {
      const userData = JSON.parse(user);
      return Promise.resolve({
        id: userData.id,
        fullName: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
        avatar: userData.avatar,
      });
    } catch (parseError) {
      return Promise.reject();
    }
  },
};
